/**
 * 批量导入的**状态机（纯函数）**：粘贴 → 预览 → 提交。
 *
 * ## 为什么单独抽出来（而不是写在页面里）
 *
 * 这一步有三条**必须成立**的性质，而它们都是"状态"问题，不是"渲染"问题：
 *
 * 1. **预览与提交必须是同一份输入**。
 *    后端的 `import/preview` 与 `import/commit` 共用同一条代码路径（只有最后写不写库
 *    不同），所以"预览说新增 8 条"就等于"提交会新增 8 条" —— **前提是输入一字未改**。
 *    页面上如果让家长重新粘一次（或者提交时重新从 textarea 里读一遍），
 *    这个保证就断了。所以这里把**预览时用的那份 payload** 整个存进状态，
 *    提交直接用它（`commitPayloadOf`），不从输入重算。
 *
 * 2. **改动了就必须重新预览**。
 *    任何一处输入变了（文本、默认课本、默认年级、默认学期、是否自动建课本），
 *    签名就变，`canCommitImport()` 立刻返回 false 并给出**具体原因**。
 *    ⛔ 不要"自动重新预览"（家长还在打字时打接口是浪费），也不要"默默用旧预览提交"。
 *
 * 3. **提交过就不能再点**。
 *    同一份文本提交两次在后端是幂等的（第二次 `created: 0`、全部 `skipped`），
 *    但界面上"再点一下"完全没有意义，所以提交成功后进入 `done`，不再允许提交。
 *
 * 这三条都是**纯函数**能表达的东西，因此它们有单测（见 `dictationImport.test.ts`），
 * 而不是靠"页面看起来对"。
 *
 * ⚠️ 请求体怎么构造**不在这里**：见 `lib/dictationWrite.ts` 的 `buildImportRequest()`
 *   （同一层纯函数，还负责课文/字/词的写载荷）。这里只管签名、校验与状态迁移。
 *
 * ⚠️ **它只服务于管理后台**（导入是维护类动作，只有系统管理员能做）：
 *   `ImportPayload` 因此来自 `api/dictationAdmin`，而不是家长端那份读接口。
 */
import type { ImportPayload } from './api/dictationAdmin';
import type { ImportReportView } from './api/dictation';
import type { ImportIssueRow } from './importIssues';

/** 一个导入步骤。 */
export interface ImportStep {
  /** `preview` 一行都不写；`commit` 会真的写库。 */
  kind: 'preview' | 'commit';
  /** 成功时的报告（`null` = 失败了）。 */
  report: ImportReportView | null;
  /** 失败时的原因（`null` = 成功了）。**这一句已经是后端给的"最具体"那句**。 */
  error: string | null;
  /**
   * 失败时后端 `error.details` 里的**逐条原因**（行号 + 词元 + 原文 + 原因）。
   *
   * ⚠️ 为什么 `error` 之外还要带它（2026-10-16 用户实测）：后端抛 `AppError` 时
   *    `message` 是**按错误码给的通用文案**（"提交的内容有误，请检查后重试"），
   *    真正的原因在 `details` 里。改造前界面只读 `message` ⇒ details 被整块丢掉，
   *    家长看到的永远是那句笼统的话（看不出哪一行、哪个词元）。
   *    `error` 负责"一句话说清"，这里负责"**逐条**说清"。
   */
  details?: ImportIssueRow[];
  /** 正在请求中。 */
  pending: boolean;
}

/** 导入输入（界面上那几个控件）。 */
export interface ImportInput {
  text: string;
  /** 空串 = 不指定（界面用空串表示"没选"，发请求时**不发这个键**）。 */
  textbook: string;
  /** 默认年级 / 学期：课本按册建（一本书 = 一册），按名字给课本时必须能确定是哪一册。 */
  gradeCode: string;
  semester: string;
}

/** 后端 `importPreviewSchema` 的硬上限（超过它整个请求会被拒）。 */
export const IMPORT_TEXT_MAX = 500_000;

/** 后端 `importCommitSchema.maxRows` 的上限。 */
export const IMPORT_MAX_ROWS = 5000;

export interface ImportState {
  input: ImportInput;
  /** 上一次预览/提交**实际发出去**的那份入参（含 `text` 原文）。 */
  previewPayload: ImportPayload | null;
  /** 预览用的那份输入的签名（与当前输入不一致 = 输入改过了）。 */
  previewSignature: string | null;
  preview: ImportStep;
  commit: ImportStep;
  /** 成功提交过的文本（用于"不用重新粘"提示里的"恢复上次内容"）。 */
  lastCommittedText: string | null;
  /** 最后一次提交成功时的报告（在 `commit.report` 之外再留一份，切 tab 也不丢）。 */
  lastCommitReport: ImportReportView | null;
}

export function emptyImportInput(text = ''): ImportInput {
  return { text, textbook: '', gradeCode: '', semester: '' };
}

export function initialImportState(text = ''): ImportState {
  return {
    input: emptyImportInput(text),
    previewPayload: null,
    previewSignature: null,
    preview: { kind: 'preview', report: null, error: null, pending: false },
    commit: { kind: 'commit', report: null, error: null, pending: false },
    lastCommittedText: null,
    lastCommitReport: null,
  };
}

/** 明确写出"哪些字段参与签名"，免得以后加了字段忘了带上（忘了 = 改动了还能提交）。 */
function signatureOf(input: ImportInput): string {
  return [
    input.text,
    `textbook=${input.textbook.trim()}`,
    /**
     * ⚠️ 年级 / 学期**必须**进签名：课本按册建，改了册别就是另一本书 ——
     *    改了却不重新预览，提交的就会是"另一册"的那一份。
     * ⛔ 版次已经彻底不用（2026-10-18），不在签名里。
     */
    `grade=${input.gradeCode}`,
    `semester=${input.semester}`,
  ].join('\u0000');
}

/** 当前输入的签名（界面上用来显示"输入已改动，请重新预览"）。 */
export function importSignature(input: ImportInput): string {
  return signatureOf(input);
}

/**
 * 构造请求体这件事**不在这里**：它在 `lib/dictationWrite.ts` 的
 * `buildImportRequest()`（同一个纯函数层，还负责课文/字/词的写载荷）。
 * 这里只留"签名 / 校验 / 状态迁移"—— 也就是真正属于状态机的东西。
 *
 * 输入本身是否合法（能发预览）。返回 `null` = 没问题。
 */
export function validateImportInput(input: ImportInput): string | null {
  if (input.text.trim() === '') return '请先粘贴要导入的内容。';
  /**
   * 课本按册建（一本书 = 一册）：指定了课本（按名字）就必须同时指定年级与学期，
   * 否则服务层无法确定是哪一册，只能逐行报"缺年级/学期"。
   * ⛔ 版次不再需要（2026-10-18）。
   */
  if (input.textbook.trim() !== '' && (input.gradeCode === '' || input.semester === '')) {
    return '选中了课本就还要选年级和学期 —— 课本按「册」建（一年级上册和一年级下册是两本书）。';
  }
  if (input.text.length > IMPORT_TEXT_MAX) {
    return `一次最多 ${IMPORT_TEXT_MAX.toLocaleString('zh-CN')} 个字符，当前 ${input.text.length.toLocaleString('zh-CN')} 个 —— 请分批导入。`;
  }
  return null;
}

// ---------------------------------------------------------------- 状态迁移

/** 改输入：**清掉预览与提交结果**（因为它们的输入已经不是这一份了）。 */
export function setImportInput(state: ImportState, patch: Partial<ImportInput>): ImportState {
  return {
    ...state,
    input: { ...state.input, ...patch },
    preview: { kind: 'preview', report: null, error: null, pending: false },
    commit: { kind: 'commit', report: null, error: null, pending: false },
  };
}

/** 预览开始。 */
export function startPreview(state: ImportState): ImportState {
  return {
    ...state,
    preview: { kind: 'preview', report: null, error: null, pending: true },
    commit: { kind: 'commit', report: null, error: null, pending: false },
  };
}

/**
 * 预览成功：把**这次真正发出去的 payload**与签名一起记下来。
 * `payload` 由调用方传入（而不是在这里重新 `buildImportPayload`），
 * 这样"发的"与"记的"必然是同一个对象。
 */
export function previewSucceeded(
  state: ImportState,
  payload: ImportPayload,
  report: ImportReportView,
): ImportState {
  return {
    ...state,
    previewPayload: payload,
    previewSignature: signatureOf(state.input),
    preview: { kind: 'preview', report, error: null, pending: false },
    commit: { kind: 'commit', report: null, error: null, pending: false },
  };
}

export function previewFailed(
  state: ImportState,
  error: string,
  details: readonly ImportIssueRow[] = [],
): ImportState {
  return {
    ...state,
    previewPayload: null,
    previewSignature: null,
    preview: { kind: 'preview', report: null, error, details: [...details], pending: false },
  };
}

export function startCommit(state: ImportState): ImportState {
  return { ...state, commit: { kind: 'commit', report: null, error: null, pending: true } };
}

export function commitSucceeded(state: ImportState, report: ImportReportView): ImportState {
  return {
    ...state,
    commit: { kind: 'commit', report, error: null, pending: false },
    lastCommitReport: report,
    lastCommittedText: state.input.text,
  };
}

export function commitFailed(
  state: ImportState,
  error: string,
  details: readonly ImportIssueRow[] = [],
): ImportState {
  return {
    ...state,
    commit: { kind: 'commit', report: null, error, details: [...details], pending: false },
  };
}

/** 放弃这次导入（清空输入 + 全部结果）。 */
export function resetImport(_state: ImportState): ImportState {
  return initialImportState('');
}

/** 把"上次提交过的内容"填回输入框（家长想接着往里加东西时用）。 */
export function restoreLastCommittedText(state: ImportState): ImportState {
  if (state.lastCommittedText === null) return state;
  return setImportInput(state, { text: state.lastCommittedText });
}

// ---------------------------------------------------------------- 判据

/** 输入是否已经与"预览过的那份"不同。 */
export function isImportDirty(state: ImportState): boolean {
  if (state.previewSignature === null) return false;
  return state.previewSignature !== signatureOf(state.input);
}

export interface CommitGate {
  allowed: boolean;
  /** 不允许时的**人话**原因（直接显示在按钮旁边）。 */
  reason: string;
  /** 真正要发给 `import/commit` 的入参（用**预览那一份**，不从输入重算）。 */
  payload: ImportPayload | null;
}

/**
 * 能不能提交，以及提交**发什么**。
 *
 * ⚠️ 返回的 `payload` 来自 `previewPayload`（预览时那一份），而不是当前的输入 ——
 *   这是"预览与提交是同一份输入"这条性质的落点。输入改过时 `allowed=false`，
 *   家长必须先重新预览（那时 `previewPayload` 才会换成新的）。
 */
export function canCommitImport(state: ImportState): CommitGate {
  if (state.commit.pending) {
    return { allowed: false, reason: '正在提交，请稍候。', payload: null };
  }
  if (state.commit.report !== null) {
    return { allowed: false, reason: '这一份已经导入过了。', payload: null };
  }
  if (state.preview.pending) {
    return { allowed: false, reason: '正在预览，请稍候。', payload: null };
  }
  /**
   * ⚠️ "输入改动过"必须排在"还没预览过"**之前**判断。
   *
   * 两者都会让 `preview.report === null`（一改输入就清掉预览结果），但要说的话
   * 完全不同：家长刚刚明明预览过、只是又改了一个字，你告诉他"请先点预览"会让他
   * 以为自己没点过。所以先算 `dirty`（它只看签名，不依赖预览结果还在不在）。
   */
  const dirty = isImportDirty(state);
  if (state.preview.report === null) {
    return {
      allowed: false,
      reason: dirty
        ? '输入在上次预览之后改动过了 —— 请重新预览，否则提交的内容与你看到的不一致。'
        : state.preview.error === null
          ? '请先点「预览」，看清会新增/跳过哪些行、哪几行有问题，再提交。'
          : '上一次预览失败了，请修好输入后重新预览。',
      payload: null,
    };
  }
  if (dirty) {
    return {
      allowed: false,
      reason: '输入在上次预览之后改动过了 —— 请重新预览，否则提交的内容与你看到的不一致。',
      payload: null,
    };
  }
  const payload = state.previewPayload;
  if (payload === null) {
    return { allowed: false, reason: '请先预览。', payload: null };
  }
  if (payload.text.length > IMPORT_TEXT_MAX) {
    return {
      allowed: false,
      reason: `一次最多 ${IMPORT_TEXT_MAX.toLocaleString('zh-CN')} 个字符，请分批导入。`,
      payload: null,
    };
  }
  return { allowed: true, reason: '', payload };
}

/**
 * 提交前给家长看的一句确认话术（把预览里的数字念一遍）。
 *
 * 拿不到的数字显示「—」，**不写成 0** —— 预览报告缺字段时，家长看到的应该是
 * "有一项没拿到"，而不是"会新增 0 条"。
 */
export function describePreviewForConfirm(report: ImportReportView): string {
  const n = (value: number | null): string => (value === null ? '—' : String(value));
  return `将新增 ${n(report.summary.created)} 条、更新 ${n(report.summary.updated)} 条、已存在跳过 ${n(report.summary.skipped)} 条；有问题的行 ${n(report.summary.errors)} 行（这些行不会被导入，其余照常导入）。`;
}

/** 提交/预览结果里"被截断"的提示（>0 时必须说人话）。 */
export function describeTruncation(report: ImportReportView, kind: 'preview' | 'commit'): string {
  const truncated = report.truncated;
  if (truncated === null || truncated <= 0) return '';
  return kind === 'preview'
    ? `还有 ${truncated} 行没有进入这次预览（一次最多处理 ${IMPORT_MAX_ROWS} 行）—— 请分批导入。`
    : `还有 ${truncated} 行没有被写入（一次最多写 ${IMPORT_MAX_ROWS} 行）—— 请再导入一次剩下的部分。`;
}

/** 导入报告里的一条问题（与 `api/dictation.ts` 的 `ImportIssueView` 同形）。 */
export interface ImportIssueLike {
  line: number;
  raw: string;
  message: string;
  /**
   * 出问题的那个**词元**（`天 天地` 里的 `天地`）。
   *
   * ⚠️ 三张表的准入规则是**逐个词元**判的（识字表/写字表只能单字、
   *    词语表只能 ≥2 字），所以错误必须说清是**哪一个**词元 ——
   *    一行可能粘了 20 个字元。
   *    `null` = 这是整行级的问题（缺年级/学期、课文行是空的……）。
   */
  token: string | null;
}

/** 把问题列表分成"整份文本级"（`line <= 0`）与"具体某一行"。 */
export function splitImportIssues(issues: ImportIssueLike[] | null): {
  textLevel: ImportIssueLike[];
  rowLevel: ImportIssueLike[];
} {
  if (issues === null) return { textLevel: [], rowLevel: [] };
  return {
    textLevel: issues.filter((issue) => issue.line <= 0),
    rowLevel: issues.filter((issue) => issue.line > 0),
  };
}
